/**
 * Workspace branding runtime (tier offer: Pro logo + colors, Enterprise white
 * label). The tenant's `styling` rides on the auth payload.
 *
 * Applying a brand color means overriding the WHOLE primary ramp, not just the
 * mid tone: `primary-500` followed the brand variable, but every hover, tint
 * and dark-mode shade (600, 400, 350, 700 …) was a hardcoded Tereiko purple,
 * so a re-skinned workspace still had purple buttons (Aleix, 2026-09-10).
 * Each shade keeps the brand's hue and saturation and borrows the lightness of
 * the Tereiko scale, so any brand color lands on a ramp with the same rhythm —
 * the same reason the tints read as one family rather than twelve colors.
 *
 * An event lets the Settings form apply changes instantly without waiting for
 * a session re-hydrate.
 */
import type { TenantStyling } from '../types/identity';

const BRANDING_EVENT = 'tereiko:branding-updated';

/** Lightness of each step of the two Tereiko ramps (see figma-tokens), and of
 *  the 500 each brand color replaces. A brand color is placed AT the anchor and
 *  the rest of the ramp is stretched around it. */
const RAMPS = {
  primary: {
    anchor: 0.592,
    steps: {
      '50': 0.939,
      '100': 0.88,
      '200': 0.757,
      '300': 0.716,
      '350': 0.698,
      '400': 0.675,
      '600': 0.535,
      '700': 0.475,
      '800': 0.418,
      '900': 0.357,
      '950': 0.178,
    } as Record<string, number>,
  },
  secondary: {
    anchor: 0.618,
    steps: {
      '50': 0.953,
      '100': 0.904,
      '200': 0.806,
      '300': 0.765,
      '400': 0.72,
      '600': 0.557,
      '700': 0.494,
      '800': 0.435,
      '900': 0.375,
      '950': 0.188,
    } as Record<string, number>,
  },
} as const;

/** '#845AD4' → '132 90 212' (the space-separated triplet our CSS vars use). */
export function hexToTriplet(hex: string): string | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h, s, l };
}

function hslToTriplet(h: number, s: number, l: number): string {
  if (s === 0) {
    const v = Math.round(l * 255);
    return `${v} ${v} ${v}`;
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)]
    .map((v) => Math.round(v * 255))
    .join(' ');
}

/**
 * Where the ink flips from white to black.
 *
 * A brand fill is the same color in light and dark mode — the button does not
 * change when the theme does — so one line serves both. It is expressed in
 * WCAG relative luminance, and the line falls between the greys #949494
 * and #959595: a brand color from #959595 up takes black text, anything
 * darker keeps white.
 *
 * Why here and not at "whichever has more contrast" (which would sit at
 * #757575): white-on-mid-tone is the convention every button in this product
 * was designed around, including Tereiko's own purple, so white is kept while
 * it still clears 3:1 — the WCAG floor for interface components. Past this
 * line white collapses (Tereiko's orange #EE8E4D was carrying white at 2.44:1)
 * and black takes over, never below 7:1.
 *
 * Aleix, 2026-09-11: "define what hex we consider dark and what we consider
 * light to render the text over the buttons and cards either black or white."
 *
 * The dark ink is the app's own near-black #26252D rather than #000: pure
 * black on a bright fill reads as a hole ("a very very dark black and a bit
 * unsettling", 2026-09-11). It costs contrast — the floor goes from 7:1 to
 * 4.77:1 at the flip point — which still clears AA for normal text.
 */
/** #26252D — `--color-text-primary` in light mode. */
export const INK_DARK = '38 37 45';
export const INK_LIGHT = '255 255 255';
const INK_FLIP_LUMINANCE = 1.05 / 3 - 0.05; // white text at exactly 3:1 → #959595

/** WCAG relative luminance of one 'r g b' triplet. */
function luminanceOf(r: number, g: number, b: number): number {
  const channel = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Black or white, whichever stays readable on the given 'r g b' fill. */
export function inkForTriplet(triplet: string): string {
  const [r, g, b] = triplet.split(' ').map(Number);
  return luminanceOf(r, g, b) > INK_FLIP_LUMINANCE ? INK_DARK : INK_LIGHT;
}

/** Same rule, from a '#rrggbb' brand color. */
export function readableInkFor(hex: string): string {
  const triplet = hexToTriplet(hex);
  return triplet ? inkForTriplet(triplet) : INK_LIGHT;
}

/** Paint one ramp (or clear it) from a single brand color. */
function applyRamp(
  root: HTMLElement,
  which: keyof typeof RAMPS,
  hex: string | null | undefined
): void {
  const { anchor, steps } = RAMPS[which];
  const base = `--color-${which}`;
  const triplet = hex ? hexToTriplet(hex) : null;
  const hsl = hex ? hexToHsl(hex) : null;

  if (!triplet || !hsl) {
    // Fall back to the theme's own values (light/dark handled in index.css).
    root.style.removeProperty(base);
    root.style.removeProperty(`${base}-ink`);
    root.style.removeProperty(`${base}-500-ink`);
    Object.keys(steps).forEach((shade) => {
      root.style.removeProperty(`${base}-${shade}`);
      root.style.removeProperty(`${base}-${shade}-ink`);
    });
    return;
  }

  root.style.setProperty(base, triplet);
  const ink = readableInkFor(hex as string);
  root.style.setProperty(`${base}-ink`, ink);
  // 500 IS the chosen color — it is the anchor, so it never appears in the
  // generated steps below and its ink would otherwise keep the Tereiko
  // default. That is the shade almost every button uses, which is exactly
  // where white text survived on a bright brand (Aleix, 2026-09-10).
  root.style.setProperty(`${base}-500-ink`, ink);
  Object.entries(steps).forEach(([shade, refL]) => {
    // The chosen color IS the 500, so the rest of the ramp is stretched around
    // it rather than pinned to Tereiko's own lightness values: otherwise a
    // dark brand color ends up lighter at 900 than at 500 and the ramp
    // inverts. Steps keep their relative spacing and their order.
    const l =
      refL >= anchor
        ? hsl.l + ((refL - anchor) * (1 - hsl.l)) / (1 - anchor)
        : hsl.l - ((anchor - refL) * hsl.l) / anchor;
    const clamped = Math.min(0.98, Math.max(0.02, l));
    const rgb = hslToTriplet(hsl.h, hsl.s, clamped);
    root.style.setProperty(`${base}-${shade}`, rgb);
    // Each step carries the ink that stays readable ON it, so a tint gets dark
    // text and a deep shade gets light text without any component knowing.
    root.style.setProperty(`${base}-${shade}-ink`, inkForTriplet(rgb));
  });
}


/* ── Large decorative surfaces ────────────────────────────────────────────
   A footer wash or a welcome banner spreads several brand shades — sometimes
   from both families — under one block of text. No single shade's ink is
   right for it: keyed to the first stop, the welcome banner went white over
   a yellow half (Aleix, 2026-09-11, "the welcome banner is still white").

   So each such surface declares the stops it actually paints, and gets one
   ink chosen from their composite. The stops live here, next to the maths,
   and must be kept in step with the CSS that paints them (index.css
   `.brand-gradient`, and the two welcome banners).                        */

type Stop = [family: keyof typeof RAMPS, shade: string, alpha: number];

interface SurfaceRecipe {
  /** Page colour the stops are laid over. */
  base: [number, number, number];
  stops: Stop[];
}

const WHITE_SURFACE: [number, number, number] = [255, 255, 255];
const DARK_SURFACE: [number, number, number] = [28, 28, 29];

const SURFACES: Record<string, { light: SurfaceRecipe; dark: SurfaceRecipe }> = {
  /**
   * The learner welcome banner's desktop radial, which is NOT theme-switched:
   * one arbitrary gradient, `primary-400` at its centre through `primary-600`
   * and out to `primary-950`. The greeting starts at `pl-[280px]`, over the
   * light middle — so those are the stops that decide, not the dark rim.
   */
  'banner-learner': {
    light: {
      base: WHITE_SURFACE,
      stops: [
        ['primary', '400', 1],
        ['primary', '600', 1],
      ],
    },
    dark: {
      base: WHITE_SURFACE,
      stops: [
        ['primary', '400', 1],
        ['primary', '600', 1],
      ],
    },
  },
  /** The trainer welcome banner. */
  banner: {
    light: {
      base: WHITE_SURFACE,
      stops: [
        ['secondary', '400', 1],
        ['primary', '500', 1],
        ['primary', '700', 1],
      ],
    },
    dark: {
      base: DARK_SURFACE,
      stops: [
        ['secondary', '600', 1],
        ['primary', '600', 1],
        ['primary', '800', 1],
      ],
    },
  },
};

/* ── The house wash (footer, chat rails, module surfaces) ────────────────
   Built from the brand's OWN hue and saturation at a fixed lightness, not
   from pale ramp tints. Tints failed the moment a workspace picked a light
   colour: shade 200 of a light green is already near-white, so at 60% over a
   white page the footer showed nothing and read as broken (Aleix, 2026-09-12:
   "i chose light green and the footer is still white ... why cant we program
   it for ANY color?").

   Pinning the lightness instead means any hue lands at the same visible
   distance from the page — light or dark, saturated or not — and the ink is
   then read off the result like every other surface. A colourless brand
   (white, black, grey) has no hue to show and lands on the same neutral band,
   which is the honest answer rather than an invisible one. */
const BAND_STOPS = {
  light: [
    { family: 'primary', lightness: 0.86 },
    { family: 'secondary', lightness: 0.9 },
    { family: 'primary', lightness: 0.81 },
  ],
  dark: [
    { family: 'primary', lightness: 0.3 },
    { family: 'secondary', lightness: 0.26 },
    { family: 'primary', lightness: 0.22 },
  ],
} as const;

/** Minimum saturation for a band stop, so a muted brand still tints. */
const BAND_MIN_SATURATION = 0.18;

/** The house colours, used for whichever family a workspace has not set. */
const TEREIKO_BRAND: Record<string, string> = {
  primary: '#845AD4',
  secondary: '#EE8E4D',
};

function bandStops(
  theme: 'light' | 'dark',
  hslOf: (family: keyof typeof RAMPS) => { h: number; s: number; l: number } | null
): string[] | null {
  const out: string[] = [];
  for (const stop of BAND_STOPS[theme]) {
    const hsl = hslOf(stop.family as keyof typeof RAMPS)
      ?? hslOf(stop.family === 'primary' ? 'secondary' : 'primary');
    if (!hsl) return null;
    // A brand with no hue (white/black/grey) keeps s = 0 and lands neutral;
    // anything with colour in it is lifted to a saturation that reads.
    const s = hsl.s === 0 ? 0 : Math.max(hsl.s, BAND_MIN_SATURATION);
    out.push(hslToTriplet(hsl.h, s, stop.lightness));
  }
  return out;
}

/** Average the stops over their base — good enough to choose an ink. */
function compositeSurface(
  recipe: SurfaceRecipe,
  shadeOf: (family: keyof typeof RAMPS, shade: string) => [number, number, number] | null
): string | null {
  // Skip stops we cannot resolve rather than giving up on the surface: a
  // workspace that re-skins only the primary still gets a composite from its
  // primary stops, instead of silently falling back to the Tereiko ink.
  const mixed = recipe.stops
    .map((stop) => {
      const rgb = shadeOf(stop[0], stop[1]);
      return rgb
        ? rgb.map((c, i) => c * stop[2] + recipe.base[i] * (1 - stop[2]))
        : null;
    })
    .filter((m): m is number[] => m !== null);
  if (mixed.length === 0) return null;
  const channels = [0, 1, 2].map((i) =>
    Math.round(mixed.reduce((sum, m) => sum + m[i], 0) / mixed.length)
  );
  return channels.join(' ');
}

/**
 * Give every declared surface the ink its composite calls for, per theme.
 * CSS picks between the two under `:root` and `.dark` (see index.css).
 */
function applySurfaceInks(
  root: HTMLElement,
  shadeOf: (family: keyof typeof RAMPS, shade: string) => [number, number, number] | null,
  hslOf: (family: keyof typeof RAMPS) => { h: number; s: number; l: number } | null
): void {
  Object.entries(SURFACES).forEach(([name, themes]) => {
    (['light', 'dark'] as const).forEach((theme) => {
      const variable = `--color-${name}-ink-${theme}`;
      const composite = compositeSurface(themes[theme], shadeOf);
      if (composite) root.style.setProperty(variable, inkForTriplet(composite));
      else root.style.removeProperty(variable);
    });
  });

  // The wash paints itself, and then tells the text what it turned out to be.
  (['light', 'dark'] as const).forEach((theme) => {
    const stops = bandStops(theme, hslOf);
    if (!stops) {
      [1, 2, 3].forEach((i) => root.style.removeProperty(`--color-band-${i}-${theme}`));
      root.style.removeProperty(`--color-band-ink-${theme}`);
      return;
    }
    stops.forEach((triplet, i) => {
      root.style.setProperty(`--color-band-${i + 1}-${theme}`, triplet);
    });
    // The band is opaque enough that its own stops decide the ink; average
    // them the same way the other surfaces are averaged.
    const channels = [0, 1, 2].map((channel) =>
      Math.round(
        stops.reduce((sum, t) => sum + Number(t.split(' ')[channel]), 0) / stops.length
      )
    );
    root.style.setProperty(`--color-band-ink-${theme}`, inkForTriplet(channels.join(' ')));
  });
}

/**
 * Resolve a shade to its live RGB — the inline override when the workspace has
 * re-skinned that family, otherwise the theme's own value. That way a surface
 * mixing a branded primary with an unbranded secondary still composites.
 */
function shadeResolver(
  root: HTMLElement
): (family: keyof typeof RAMPS, shade: string) => [number, number, number] | null {
  const computed = window.getComputedStyle(root);
  return (family, shade) => {
    const name = `--color-${family}-${shade}`;
    const raw = (
      root.style.getPropertyValue(name) || computed.getPropertyValue(name)
    ).trim();
    const parts = raw.split(/\s+/).map(Number);
    return parts.length === 3 && parts.every(Number.isFinite)
      ? (parts as [number, number, number])
      : null;
  };
}

/** Apply (or clear) the workspace's brand colors across both ramps. */
export function applyBranding(styling: TenantStyling | undefined | null): void {
  const root = document.documentElement;
  applyRamp(root, 'primary', styling?.primary_color);
  applyRamp(root, 'secondary', styling?.secondary_color);

  if (styling?.primary_color || styling?.secondary_color) {
    // An unbranded family still contributes its Tereiko hue, so a workspace
    // that sets only a primary gets a coherent band rather than none.
    const hslOf = (family: keyof typeof RAMPS) =>
      hexToHsl(
        (family === 'primary' ? styling.primary_color : styling.secondary_color)
          ?? TEREIKO_BRAND[family]
      );
    applySurfaceInks(root, shadeResolver(root), hslOf);
  } else {
    Object.keys(SURFACES).forEach((name) => {
      root.style.removeProperty(`--color-${name}-ink-light`);
      root.style.removeProperty(`--color-${name}-ink-dark`);
    });
    (['light', 'dark'] as const).forEach((theme) => {
      [1, 2, 3].forEach((i) => root.style.removeProperty(`--color-band-${i}-${theme}`));
      root.style.removeProperty(`--color-band-ink-${theme}`);
    });
  }
}

export function announceBrandingUpdate(styling: TenantStyling): void {
  window.dispatchEvent(new CustomEvent(BRANDING_EVENT, { detail: styling }));
}

export function subscribeBranding(handler: (styling: TenantStyling) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<TenantStyling>).detail);
  window.addEventListener(BRANDING_EVENT, listener);
  return () => window.removeEventListener(BRANDING_EVENT, listener);
}
